/**
 * MeraGym Analytics Events
 * Centralised wrapper for Meta Pixel (fbq) and Google Analytics 4 (gtag).
 * Both are already initialised in public/index.html — do NOT re-init here.
 *
 * Meta Pixel ID : 340929571958943
 * GA4 ID        : G-81WRXV62G9
 * GTM ID        : GTM-N47SWJQW
 */

// ─── UTM capture ────────────────────────────────────────────────────────────

/** Call on every route change (App.js) to capture UTM params into sessionStorage */
export const captureUTM = () => {
  try {
    const params = new URLSearchParams(window.location.search);
    ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term'].forEach((k) => {
      const v = params.get(k);
      if (v) sessionStorage.setItem(k, v);
    });
  } catch (_) {}
};

/** Read stored UTM params (send with order payload) */
export const getUTM = () => ({
  utm_source:   sessionStorage.getItem('utm_source')   || null,
  utm_medium:   sessionStorage.getItem('utm_medium')   || null,
  utm_campaign: sessionStorage.getItem('utm_campaign') || null,
});

// ─── Helpers ─────────────────────────────────────────────────────────────────

const fbq = (...args) => {
  try { if (window.fbq) window.fbq(...args); } catch (_) {}
};

const gtag = (...args) => {
  try { if (window.gtag) window.gtag(...args); } catch (_) {}
};

const dlPush = (obj) => {
  try { if (window.dataLayer) window.dataLayer.push(obj); } catch (_) {}
};

// ─── Product View ─────────────────────────────────────────────────────────────

/**
 * Fire on ProductDetail mount.
 * @param {object} product  — API product object
 */
export const trackViewContent = (product) => {
  if (!product) return;

  fbq('track', 'ViewContent', {
    content_ids:  [String(product.id)],
    content_type: 'product',
    content_name: product.title,
    value:        Number(product.discount_price) || 0,
    currency:     'INR',
  });

  gtag('event', 'view_item', {
    currency: 'INR',
    value:    Number(product.discount_price) || 0,
    items: [{
      item_id:       String(product.id),
      item_name:     product.title,
      item_category: product.category?.title || '',
      price:         Number(product.discount_price) || 0,
      quantity:      1,
    }],
  });

  dlPush({
    event:      'view_item',
    ecommerce: {
      currency: 'INR',
      value:    Number(product.discount_price) || 0,
      items: [{
        item_id:   String(product.id),
        item_name: product.title,
        price:     Number(product.discount_price) || 0,
        quantity:  1,
      }],
    },
  });
};

// ─── Add to Cart ─────────────────────────────────────────────────────────────

/**
 * Fire when user adds a product to cart.
 * @param {object} product  — API product object
 * @param {object|null} variant — selected variant or null
 * @param {number} quantity
 */
export const trackAddToCart = (product, variant = null, quantity = 1) => {
  if (!product) return;

  const price = Number(variant?.discount_price || product.discount_price) || 0;
  const value = price * quantity;

  fbq('track', 'AddToCart', {
    content_ids:  [String(product.id)],
    content_type: 'product',
    content_name: product.title,
    value:        value,
    currency:     'INR',
  });

  gtag('event', 'add_to_cart', {
    currency: 'INR',
    value:    value,
    items: [{
      item_id:       String(product.id),
      item_name:     product.title,
      item_category: product.category?.title || '',
      item_variant:  variant ? `${variant.label}: ${variant.value}` : undefined,
      price:         price,
      quantity:      quantity,
    }],
  });

  dlPush({
    event:     'add_to_cart',
    ecommerce: {
      currency: 'INR',
      value:    value,
      items: [{
        item_id:  String(product.id),
        item_name: product.title,
        price:    price,
        quantity: quantity,
      }],
    },
  });
};

// ─── Initiate Checkout ────────────────────────────────────────────────────────

/**
 * Fire when checkout page loads.
 * @param {Array} cartItems — items from CartSlice
 * @param {number} total    — cart total
 */
export const trackInitiateCheckout = (cartItems, total) => {
  if (!cartItems?.length) return;

  fbq('track', 'InitiateCheckout', {
    content_ids: cartItems.map((i) => String(i.id)),
    num_items:   cartItems.reduce((s, i) => s + i.qty, 0),
    value:       Number(total) || 0,
    currency:    'INR',
  });

  gtag('event', 'begin_checkout', {
    currency: 'INR',
    value:    Number(total) || 0,
    items:    cartItems.map((i) => ({
      item_id:      String(i.id),
      item_name:    i.title,
      item_variant: i.variantInfo || undefined,
      price:        i.price,
      quantity:     i.qty,
    })),
  });

  dlPush({
    event:     'begin_checkout',
    ecommerce: {
      currency: 'INR',
      value:    Number(total) || 0,
      items:    cartItems.map((i) => ({
        item_id:  String(i.id),
        item_name: i.title,
        price:    i.price,
        quantity: i.qty,
      })),
    },
  });
};

// ─── Purchase (Conversion) ───────────────────────────────────────────────────

/**
 * THE MAIN CONVERSION EVENT — fire once on OrderConfirmation mount.
 * @param {object} order — order object returned by API (order_number, total_amount, items[])
 */
export const trackPurchase = (order) => {
  if (!order) return;

  const items = (order.items || []).map((i) => ({
    item_id:      String(i.product_id),
    item_name:    i.product_title,
    item_variant: i.variant_info || undefined,
    price:        Number(i.price) || 0,
    quantity:     Number(i.quantity) || 1,
  }));

  // Meta Pixel Purchase
  fbq('track', 'Purchase', {
    content_ids:  (order.items || []).map((i) => String(i.product_id)),
    content_type: 'product',
    value:        Number(order.total_amount) || 0,
    currency:     'INR',
    num_items:    (order.items || []).reduce((s, i) => s + (i.quantity || 1), 0),
  });

  // GA4 purchase
  gtag('event', 'purchase', {
    transaction_id: order.order_number,
    value:          Number(order.total_amount) || 0,
    currency:       'INR',
    shipping:       Number(order.shipping_amount) || 0,
    items,
  });

  // GTM dataLayer (for Google Ads conversion tag via GTM)
  dlPush({ ecommerce: null }); // clear previous
  dlPush({
    event: 'purchase',
    ecommerce: {
      transaction_id: order.order_number,
      value:          Number(order.total_amount) || 0,
      currency:       'INR',
      shipping:       Number(order.shipping_amount) || 0,
      items,
    },
  });
};
